import React, { useMemo } from 'react';
import moment from 'moment';
import { useForm } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import FilterModal from '../../../components/ModernFilters/FilterModal';
import PeriodSelector from '../../../components/ModernFilters/PeriodSelector';
import RadioList from '../../../components/ModernFilters/RadioList';
import { translate } from '../../../services/translate';
import { accessService } from '../../../services/central-api/access';

export interface UserHistoricFiltersParams {
    page: number;
    limit: number;
    action?: string;
    responsibleUserId?: number;
    beginIn?: number;
    endIn?: number;
    textFilter?: string;
}

interface Props {
    userId: number;
    filters: UserHistoricFiltersParams;
    setFilters: React.Dispatch<React.SetStateAction<UserHistoricFiltersParams>>;
    textFilter: string;
    setTextFilter: React.Dispatch<React.SetStateAction<string>>;
}

type FiltersForm = {
    period: { from: Date | null; to: Date | null; };
    responsibleUserId: number | null;
    action: string | null;
};

function UserHistoricFilter({ userId, filters, setFilters, textFilter }: Props) {
    const form = useForm<FiltersForm>({
        mode: 'onChange',
        defaultValues: {
            period: {
                from: filters.beginIn ? moment(filters.beginIn).toDate() : null,
                to: filters.endIn ? moment(filters.endIn).toDate() : null,
            },
            responsibleUserId: filters.responsibleUserId ?? null,
            action: filters.action ?? null,
        },
    });

    const { data: users, isLoading: isLoadingUsers } = useQuery({
        queryKey: ['userHistoricFiltersResponsibleUsers'],
        queryFn: () => accessService.getAccessAllUsers({}),
    });

    const { data: userActions, isLoading: isLoadingActions } = useQuery({
        queryKey: ['userHistoricFiltersActions', userId],
        queryFn: () => accessService.getUserHistoryActions(userId),
        enabled: userId > 0,
    });

    const userItems = useMemo(() => (users ?? []).map(user => ({
        label: user.name,
        value: user.id,
    })), [users]);

    const actionItems = useMemo(() => (userActions ?? [])
        .map(action => ({
            label: translate(action),
            value: action as string,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)), [userActions]);

    const handleFilter = (values: FiltersForm) => {
        const params = {
            textFilter: textFilter !== '' ? textFilter : undefined,
            action: values.action ?? undefined,
            responsibleUserId: values.responsibleUserId ?? undefined,
            beginIn: values.period.from ? moment(values.period.from).valueOf() : undefined,
            endIn: values.period.to ? moment(values.period.to).valueOf() : undefined,
        };

        setFilters({ ...filters, page: 0, ...params });
    };

    return <FilterModal
        form={form}
        onFilters={handleFilter}
        cleanValues={{
            period: { from: null, to: null },
            responsibleUserId: null,
            action: null,
        }}
        filters={[{
            label: translate('userHistoric'),
            filters: [{
                componentHandlesCount: true,
                name: 'period',
                label: translate('period'),
                component: PeriodSelector,
            }, {
                componentHandlesCount: false,
                name: 'responsibleUserId',
                label: translate('responsible'),
                getCount: RadioList.getCount,
                component: ({ name }) => <RadioList
                    name={name}
                    items={userItems}
                    clearValue={null}
                    isLoading={isLoadingUsers}
                />,
            }, {
                componentHandlesCount: false,
                name: 'action',
                label: translate('action'),
                getCount: RadioList.getCount,
                component: ({ name }) => <RadioList
                    name={name}
                    items={actionItems}
                    clearValue={null}
                    isLoading={isLoadingActions}
                />,
            }],
        }]}
    />;
}

export default UserHistoricFilter;
